/**
 * Turning a bank's spreadsheet into transactions.
 *
 * Every bank lays its export out differently, so nothing here is hard-coded to
 * one of them: the header row is found by keyword, the columns by role, and
 * when there is no header at all the shape of the data decides. Each step is
 * reported back so the import screen can show what it decided and let the user
 * correct it.
 */
import { CURRENCIES, parseAmount, round2 } from './money.js'
import { diffDays } from './dates.js'
import { serialToISO } from './sheet-values.js'

/** Lower-cased and stripped of diacritics, so `Iznos` matches `IZNOS`. */
const fold = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()

const clean = (value) => String(value ?? '').replace(/\s+/g, ' ').trim()

/**
 * Header words per role, in several languages — a statement is as likely to
 * say `Iznos` or `Betrag` as `Amount`. Matched as substrings of the cell.
 */
const ROLE_WORDS = {
  date: ['datum', 'date', 'data', 'fecha', 'valuta', 'dan'],
  type: ['tip', 'vrsta', 'type', 'kind', 'kategorija', 'category', 'transakcij'],
  description: [
    'opis', 'description', 'details', 'detalji', 'svrha', 'naziv', 'primalac', 'payee',
    'merchant', 'narration', 'purpose', 'verwendungszweck', 'beneficiary', 'memo', 'text',
  ],
  debit: ['duguje', 'dugovanje', 'zaduzenje', 'isplata', 'odliv', 'debit', 'withdrawal', 'soll', 'outflow'],
  credit: ['potrazuje', 'potrazivanje', 'odobrenje', 'uplata', 'priliv', 'credit', 'deposit', 'haben', 'inflow'],
  amount: ['iznos', 'amount', 'value', 'betrag', 'importe', 'montant', 'promet', 'suma'],
}
const ROLES = Object.keys(ROLE_WORDS)

const CURRENCY_CODES = new Set(CURRENCIES.map((c) => c.code))
const CURRENCY_SYMBOLS = [
  ['€', 'EUR'], ['£', 'GBP'], ['¥', 'JPY'], ['дин', 'RSD'], ['zł', 'PLN'], ['kč', 'CZK'], ['kr', 'SEK'],
]

/* ------------------------------------------------------------------ */
/* cells                                                               */
/* ------------------------------------------------------------------ */

const daysInMonth = (y, m) => new Date(y, m, 0).getDate()
const pad = (n) => String(n).padStart(2, '0')

/**
 * A date cell in any of the shapes a statement uses: already-ISO, `d.m.Y`,
 * `d/m/Y`, `Y-m-d`, a two-digit year, or a bare Excel serial.
 *
 * @param {string|number|null} value
 * @param {'dmy'|'mdy'} order  which way round an ambiguous `03/04/2026` reads
 * @returns {string|null} `YYYY-MM-DD`
 */
export function parseDateCell(value, order = 'dmy') {
  if (value == null || value === '') return null
  if (typeof value === 'number') return Number.isInteger(value) ? serialToISO(value) : null

  const text = String(value).trim()
  const match = text.match(/^(\d{1,4})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*(\d{2,4})/)
  if (!match) return null

  let [, a, b, c] = match.map(Number)
  let year
  let month
  let day
  if (String(match[1]).length === 4) {
    ;[year, month, day] = [a, b, c]
  } else {
    ;[day, month] = order === 'mdy' ? [b, a] : [a, b]
    year = c
  }
  if (year < 100) year += year < 70 ? 2000 : 1900
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null
  if (year < 1900 || year > 2999) return null
  return `${year}-${pad(month)}-${pad(day)}`
}

/**
 * An amount cell. Handles a trailing minus (`1.234,56-`) on top of everything
 * `parseAmount` already copes with; returns NaN when there is no number.
 */
export function parseAmountCell(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN
  const text = String(value ?? '').trim()
  if (!text) return NaN
  const parsed = parseAmount(text)
  if (!Number.isFinite(parsed)) return NaN
  return /-\s*$/.test(text) && parsed > 0 ? -parsed : parsed
}

/** Which way round `03/04/2026` reads, decided by any unambiguous dates present. */
export function detectDateOrder(values) {
  let dayFirst = 0
  let monthFirst = 0
  for (const value of values) {
    const match = String(value ?? '').trim().match(/^(\d{1,2})\s*[.\-/]\s*(\d{1,2})\s*[.\-/]\s*\d{2,4}/)
    if (!match) continue
    if (Number(match[1]) > 12) dayFirst++
    else if (Number(match[2]) > 12) monthFirst++
  }
  return monthFirst > dayFirst ? 'mdy' : 'dmy'
}

function detectCurrency(values) {
  for (const value of values) {
    const text = String(value ?? '')
    const code = text.toUpperCase().match(/\b([A-Z]{3})\b/)?.[1]
    if (code && CURRENCY_CODES.has(code)) return code
    for (const [symbol, currency] of CURRENCY_SYMBOLS) {
      if (text.toLowerCase().includes(symbol)) return currency
    }
  }
  return null
}

/* ------------------------------------------------------------------ */
/* layout                                                              */
/* ------------------------------------------------------------------ */

function rolesInRow(row) {
  const found = {}
  row.forEach((cell, index) => {
    const text = fold(cell)
    if (!text || text.length > 40) return
    for (const role of ROLES) {
      if (found[role] !== undefined) continue
      if (ROLE_WORDS[role].some((word) => text.includes(word))) {
        found[role] = index
        return
      }
    }
  })
  return found
}

/** Columns judged by what is in them, for statements that ship without a header. */
function inferColumns(rows, order) {
  const width = rows.reduce((w, row) => Math.max(w, row.length), 0)
  const stats = []
  for (let col = 0; col < width; col++) {
    const values = rows.map((row) => row[col]).filter((v) => v != null && v !== '')
    const dates = values.filter((v) => parseDateCell(v, order)).length
    const amounts = values.filter((v) => Number.isFinite(parseAmountCell(v))).length
    const length = values.reduce((sum, v) => sum + String(v).length, 0) / (values.length || 1)
    stats.push({ col, n: values.length, dateRate: dates / (values.length || 1), amountRate: amounts / (values.length || 1), length })
  }
  const taken = new Set()
  const best = (test) => {
    const pick = stats
      .filter((s) => s.n > 0 && !taken.has(s.col) && test(s))
      .sort((a, b) => test(b) - test(a))[0]
    if (pick) taken.add(pick.col)
    return pick?.col ?? null
  }
  const date = best((s) => (s.dateRate >= 0.6 ? s.dateRate : 0))
  const amount = best((s) => (s.amountRate >= 0.6 ? s.amountRate : 0))
  const description = best((s) => s.length)
  return { date, amount, description }
}

/**
 * Finds the header row and what each column means. Returns the first candidate
 * that actually yields dated rows underneath it.
 */
function detectLayout(rows, order) {
  const candidates = []
  const limit = Math.min(rows.length, 40)
  for (let index = 0; index < limit; index++) {
    const found = rolesInRow(rows[index])
    const hasAmount = found.amount !== undefined || found.debit !== undefined || found.credit !== undefined
    if (found.date === undefined || !hasAmount) continue
    candidates.push({ index, columns: found, score: Object.keys(found).length })
  }
  candidates.sort((a, b) => b.score - a.score || a.index - b.index)

  for (const candidate of candidates) {
    const body = rows.slice(candidate.index + 1)
    const dated = body.filter((row) => parseDateCell(row[candidate.columns.date], order)).length
    if (dated > 0) return { headerRow: candidate.index, columns: candidate.columns }
  }
  return { headerRow: -1, columns: inferColumns(rows, order) }
}

/* ------------------------------------------------------------------ */
/* reading                                                             */
/* ------------------------------------------------------------------ */

/**
 * Reads a sheet's rows into transactions.
 *
 * @param {(string|number|null)[][]} rows
 * @param {object} [options]
 * @param {'dmy'|'mdy'} [options.dateOrder]  override the detected date order
 * @param {object} [options.columns]         override the detected mapping
 * @returns {{ headerRow: number, columns: object, dateOrder: string, currency: string|null,
 *            entries: object[], skipped: number }}
 */
export function readStatement(rows, options = {}) {
  const table = (rows ?? []).map((row) => (Array.isArray(row) ? row : []))
  const probe = detectLayout(table, 'dmy')
  const dateColumn = options.columns?.date ?? probe.columns.date
  const dateOrder =
    options.dateOrder ??
    detectDateOrder(table.slice(probe.headerRow + 1).map((row) => row[dateColumn ?? 0]))

  const layout = options.columns ? { headerRow: probe.headerRow, columns: options.columns } : detectLayout(table, dateOrder)
  const { headerRow } = layout
  const columns = { ...layout.columns }
  if (columns.date == null) {
    return { headerRow, columns, dateOrder, currency: null, entries: [], skipped: table.length }
  }
  // A signed amount column wins over a debit/credit pair when a sheet has both.
  const signed = columns.amount != null

  const body = table.slice(headerRow + 1)
  const entries = []
  let skipped = 0

  for (const [offset, row] of body.entries()) {
    const date = parseDateCell(row[columns.date], dateOrder)
    if (!date) {
      if (row.some((cell) => cell != null && cell !== '')) skipped++
      continue
    }
    let amount = NaN
    if (signed) {
      amount = parseAmountCell(row[columns.amount])
    } else {
      const credit = columns.credit != null ? parseAmountCell(row[columns.credit]) : NaN
      const debit = columns.debit != null ? parseAmountCell(row[columns.debit]) : NaN
      const inflow = Number.isFinite(credit) ? Math.abs(credit) : 0
      const outflow = Number.isFinite(debit) ? Math.abs(debit) : 0
      if (Number.isFinite(credit) || Number.isFinite(debit)) amount = inflow - outflow
    }
    if (!Number.isFinite(amount) || amount === 0) {
      skipped++
      continue
    }

    const type = columns.type != null ? clean(row[columns.type]) : ''
    const description = clean(columns.description != null ? row[columns.description] : '') || type || 'Imported'
    entries.push({
      row: headerRow + 1 + offset,
      date,
      description,
      amount: round2(amount),
      kind: amount > 0 ? 'income' : 'expense',
      type,
    })
  }

  const amountCells = body.map((row) => row[signed ? columns.amount : (columns.debit ?? columns.credit)])
  return {
    headerRow,
    columns,
    dateOrder,
    currency: detectCurrency(amountCells),
    entries,
    skipped,
  }
}

/** Totals for the import screen. */
export function summarise(entries) {
  const dates = entries.map((e) => e.date).sort()
  return {
    count: entries.length,
    income: round2(entries.filter((e) => e.amount > 0).reduce((sum, e) => sum + e.amount, 0)),
    spent: round2(entries.filter((e) => e.amount < 0).reduce((sum, e) => sum - e.amount, 0)),
    from: dates[0] ?? null,
    to: dates[dates.length - 1] ?? null,
  }
}

/**
 * Pending rows already in the ledger that a statement no longer lists: none of
 * its entries was matched to them (`markDuplicates` sets `matched`), and they
 * fall inside the statement's own days. The bank dropped the authorisation, or
 * settled it at an amount that made it another row. A pending row newer or
 * older than the statement is not something it can speak for, and is left.
 *
 * @param {object[]} existing  the ledger, with `settled` (0 while pending)
 * @param {object[]} entries   the statement, after `markDuplicates`
 */
export function stalePending(existing, entries) {
  if (!entries?.length) return []
  const claimed = new Set(entries.map((e) => e.matched?.id).filter(Boolean))
  const { from, to } = summarise(entries)
  return (existing ?? []).filter((tx) => tx.settled === 0 && !claimed.has(tx.id) && tx.date >= from && tx.date <= to)
}

/**
 * How two rows are decided to be the same transaction.
 *
 * `keyOf` is everything except the date; `withinDays` is how far the date may
 * move and still be the same payment. Generic insists on the same day, because
 * without knowing a bank's habits a row three days away is a different row.
 * A bank importer that knows better widens the window — see `lib/importers`.
 */
export const SAME_DAY = {
  withinDays: 0,
  keyOf: (tx) => `${round2(tx.amount).toFixed(2)}|${fold(tx.description)}`,
}

/**
 * Flags entries already in the ledger, so importing the same statement twice
 * is a no-op. Identical rows on one day are counted, not collapsed: a bank
 * statement really can hold two identical coffees, and only the third would be
 * a duplicate of an import that already brought two.
 *
 * With a window, the nearest row wins — a statement re-exported after
 * settlement moves a transaction by a day or two, and the one it moved from is
 * the one it matches, not some other coffee the same week. "Nearest" is decided
 * across the whole statement, not entry by entry: a row that is exactly on its
 * day is paired first, so an earlier entry allowed to have moved cannot take it
 * from the entry it really is and leave that one to be imported twice.
 *
 * @param {object[]} entries
 * @param {object[]} existing
 * @param {{withinDays?: number, keyOf?: Function}} [rule]
 * @returns {object[]} the entries, each with `duplicate`, the `matched` row when
 *                     there is one, and `movedBy` when it was found on another day
 */
export function markDuplicates(entries, existing = [], rule = SAME_DAY) {
  const withinDays = rule?.withinDays ?? 0
  const keyOf = rule?.keyOf ?? SAME_DAY.keyOf
  // A bank may know that only *some* of its rows can move — the ones it had not
  // settled yet. Everything else is held to the same day, because a payment
  // that was already final has no reason to be re-dated.
  const lenientWhen = rule?.lenientWhen ?? (() => true)

  const pool = new Map()
  existing.forEach((tx, order) => {
    const key = keyOf(tx)
    if (!pool.has(key)) pool.set(key, [])
    pool.get(key).push({ tx, order })
  })

  // Every pairing the rule allows, closest first; ties keep statement order,
  // then ledger order, which is what counting identical rows one-for-one needs.
  const pairs = []
  entries.forEach((entry, at) => {
    const bucket = pool.get(keyOf(entry)) ?? []
    for (const { tx, order } of bucket) {
      const gap = diffDays(tx.date, entry.date)
      // Either side may be the one that had not settled when it was exported.
      const allowed = lenientWhen(entry) || lenientWhen(tx) ? withinDays : 0
      if (Math.abs(gap) <= allowed) pairs.push({ at, tx, order, gap })
    }
  })
  pairs.sort((a, b) => Math.abs(a.gap) - Math.abs(b.gap) || a.at - b.at || a.order - b.order)

  const found = new Map()
  const taken = new Set()
  for (const pair of pairs) {
    // By position: the same row may be passed twice, as two identical rows.
    if (found.has(pair.at) || taken.has(pair.order)) continue
    found.set(pair.at, pair)
    taken.add(pair.order)
  }

  return entries.map((entry, at) => {
    const best = found.get(at)
    if (!best) return { ...entry, duplicate: false }
    return {
      ...entry,
      duplicate: true,
      // Which row it is a duplicate *of*: an unsettled one can be brought up to
      // date from this entry rather than simply skipped.
      matched: best.tx,
      ...(best.gap === 0 ? {} : { movedBy: best.gap }),
    }
  })
}
